import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AgentInvocationDetailItem, AgentInvocationsResponse } from '@trovata/app/features/ai-agents/models/agents.model';
import { CashAgentsDataProvider } from '@trovata/app/features/ai-agents/services/cash-agents-data.provider';

/** One non-success Cash run, normalized for the merged agents card and the briefing facts. */
export interface CashAgentRun {
	readonly id: string;
	readonly agent: string;
	/** The agent's own one-line summary (`title`) — the Cash analogue of TMS REF_2. */
	readonly summary: string;
	/** Full report body, inline (Cash carries no rawFileId — no second fetch exists or is needed). */
	readonly content: string;
	readonly createdAt: string | null;
	/** The raw wire status ("failed", "error", …) — kept for honest labelling. */
	readonly status: string;
}

/** The Cash half of the merged agents card: what did not succeed inside the window. */
export interface CashRunWindow {
	readonly count: number;
	readonly runs: readonly CashAgentRun[];
	/** True when the newest-50 page was exhausted INSIDE the window — the true count may be higher. */
	readonly truncated: boolean;
}

/** Statuses that mean "ran fine" or "still running" — everything ELSE is a non-success worth surfacing.
 *  The Cash wire vocabulary is model-authored freetext with only these values guaranteed
 *  (rebased `agent_invocation.py`), so v1 counts non-successes rather than guessing a severity
 *  taxonomy that does not exist server-side (design §8/§9 — approved). */
const NON_NOTABLE_STATUSES: ReadonlySet<string> = new Set<string>(['success', 'completed', 'pending', 'processing']);

/** The server's hard page cap on `GET /agents/invocations` (newest-first). */
const SERVER_PAGE_CAP: number = 50;

/**
 * The Cash side of the briefing's merged agents card: fetches the recent-invocations page and
 * reduces it to "what did not succeed inside the selected window".
 *
 * Injects {@link CashAgentsDataProvider} DIRECTLY, never through `AGENTS_DATA_PROVIDER` — that
 * token routes on `AgentsState.explicitContext`, which whatever agents surface ran last can leave
 * pointing at the wrong product; from the TMS home it would then send this Cash read to ATOM.
 * (The TMS expansion documents the same trap for the mirror case.)
 *
 * Windowing is CLIENT-side over the server's newest-50 page (the endpoint has no window param
 * today); `createdAfter` is also sent so the query tightens automatically once the API supports
 * it. When the page is exhausted inside the window the result is flagged `truncated` and every
 * consumer renders "N+" rather than under-reporting silently.
 */
@Injectable({ providedIn: 'root' })
export class CashAgentRunsSource {
	private readonly cashProvider: CashAgentsDataProvider = inject(CashAgentsDataProvider);

	/**
	 * The window's non-success runs, newest first. Emits null on ANY fetch error: the caller keeps
	 * the TMS feed intact and degrades only the Cash half (per-source failure isolation, design §9).
	 */
	fetchWindow(sinceMinutes: number): Observable<CashRunWindow | null> {
		const sinceMs: number = Date.now() - sinceMinutes * 60000;
		// FULL ISO with the Z: the column is timestamptz, and a zone-less literal resolves in the
		// SESSION's timezone — a silent window skew on any non-UTC session (review).
		const createdAfter: string = new Date(sinceMs).toISOString();
		return this.cashProvider.getRecentInvocations(undefined, createdAfter).pipe(
			map((response: AgentInvocationsResponse): CashRunWindow => {
				const pageExhausted: boolean = response.invocations.length >= SERVER_PAGE_CAP;
				const inWindow: AgentInvocationDetailItem[] = response.invocations.filter((invocation: AgentInvocationDetailItem): boolean => {
					const at: number = invocation.createdAt !== null ? Date.parse(invocation.createdAt) : NaN;
					return Number.isFinite(at) && at >= sinceMs;
				});
				const notable: AgentInvocationDetailItem[] = inWindow.filter(
					(invocation: AgentInvocationDetailItem): boolean => !NON_NOTABLE_STATUSES.has(invocation.status.toLowerCase())
				);
				return {
					count: notable.length,
					runs: notable.map(
						(invocation: AgentInvocationDetailItem): CashAgentRun => ({
							id: invocation.invocationId,
							agent: invocation.agent.name,
							summary: invocation.title,
							content: invocation.response,
							createdAt: invocation.createdAt,
							status: invocation.status,
						})
					),
					// Truncated only when the WINDOW is what hit the cap: a full page whose oldest
					// rows fall outside the window is complete for this window, not truncated.
					truncated: pageExhausted && inWindow.length >= SERVER_PAGE_CAP,
				};
			}),
			catchError((error: unknown): Observable<null> => {
				console.warn('[suggestions] cash agent runs unavailable; TMS feed unaffected', error);
				return of(null);
			})
		);
	}
}
